import { InnerBox } from "@/components/elements/InnerBox";
import { divider, black, blue, text } from "@/theme/colors";
import { Typography, Stack, Box, Collapse, Popover, Tooltip } from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useVisible } from "@/hooks/useVisible";
import { ScheduleAdjustmentModal } from "@/features/case/components/CaseSchedule/ScheduleAdjustmentModal";
import { useState, useEffect, useMemo, useCallback } from "react";
import { Button } from "@/components/elements/Button";
import { IconEditor } from "@/assets/icons";
import { Talent, TCaseScheduleForm, TCaseScheduleItem, InterviewMethod, MeetingToolType } from "@/features/case/types/case";
import { FCC } from "@/types/common";
import { convertCaseSchedule, convertCaseScheduleToFormValues } from "@/features/case/utils/convertCaseSchedule";
import { useParams } from "react-router-dom";
import {
  createCaseSchedule,
  updateCaseSchedule,
  deleteCaseSchedule,
  useCaseScheduleList,
  useCaseScheduleDetail,
} from "@/api/services/main/case-schedule";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CaseInfoItem } from "@/features/case/components/CaseBasicInfo/CaseInfoItem";
import { dayjs } from "@/utils/dayjs";
import { useCaseMatches } from "@/api/services/main/case";
import { TCaseMatches } from "@/api/services/main/case/dtos/get-case.dto";
import { DATE_FORMAT } from "@/constants/datetime";
import { IconBox } from "@/components/elements/IconBox";
import { rounded, elevation } from "@/theme/variables";
import { background } from "@/theme/colors";
import { IconCopy } from "@/assets/icons/IconCopy";
import { Tag } from "@/components/elements/Tag";
import { truncateWithEllipsis } from "@/utils/string";
import { sortTalentsByRecommended } from "@/utils/talent";

type TCaseScheduleBlockProps = {
  disabled?: boolean;
  isCompleted?: boolean;
};

const MAX_VISIBLE_TALENTS = 5;

export const CaseScheduleBlock: FCC<TCaseScheduleBlockProps> = ({ disabled = false, isCompleted = false }) => {
  const scheduleModal = useVisible();
  const editScheduleModal = useVisible();
  const [schedules, setSchedules] = useState<TCaseScheduleItem[]>([]);
  const [editingSchedule, setEditingSchedule] = useState<TCaseScheduleItem | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | undefined>(undefined);
  const [expandedSchedules, setExpandedSchedules] = useState<Record<number, boolean>>({});
  const [talentPopoverAnchor, setTalentPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverTalents, setPopoverTalents] = useState<Talent[]>([]);
  const { caseId } = useParams();
  const queryClient = useQueryClient();
  const { data: caseSchedules } = useCaseScheduleList(caseId);
  const { data: scheduleDetail } = useCaseScheduleDetail(caseId, selectedScheduleId);

  const isModalOpen = scheduleModal.visible || editScheduleModal.visible;

  const hasSchedulesWithTalents = (caseSchedules?.data || []).some((s: TCaseScheduleItem) => (s.talents?.length ?? 0) > 0);
  const { data: caseMatchesData } = useCaseMatches(Number(caseId), { page: 1, perPage: 9999 }, isModalOpen || hasSchedulesWithTalents);

  const talents: Talent[] = useMemo(() => {
    if (!caseMatchesData?.data) return [];
    return caseMatchesData.data
      .filter((caseMatch: TCaseMatches) => caseMatch.talent?.id)
      .map((caseMatch: TCaseMatches) => ({
        id: Number(caseMatch.talent?.id),
        fullName: caseMatch.talent?.fullName ?? "",
        nickname: caseMatch.talent?.nickName ?? "",
        isRecommendedTalent: caseMatch.talent?.isRecommendedTalent ?? false,
      }));
  }, [caseMatchesData]);

  // Create a lookup map for isRecommendedTalent by talent ID
  const talentRecommendedMap = useMemo(() => {
    const map = new Map<number, boolean>();
    talents.forEach((t) => map.set(t.id, t.isRecommendedTalent ?? false));
    return map;
  }, [talents]);

  useEffect(() => {
    if (caseSchedules?.data) {
      setSchedules(caseSchedules.data);
      const initialExpanded: Record<number, boolean> = {};
      caseSchedules.data.forEach((schedule: TCaseScheduleItem) => {
        if (schedule.id) {
          const isPassed =
            schedule.dateConfirm && schedule.startTimeConfirm && dayjs(`${schedule.dateConfirm} ${schedule.startTimeConfirm}`).isBefore(dayjs());
          initialExpanded[schedule.id] = !isPassed;
        }
      });
      setExpandedSchedules(initialExpanded);
    }
  }, [caseSchedules]);

  const toggleScheduleExpand = useCallback((scheduleId: number) => {
    setExpandedSchedules((prev) => ({
      ...prev,
      [scheduleId]: !prev[scheduleId],
    }));
  }, []);

  // Handle talent overflow popover
  const handleTalentOverflowClick = useCallback((event: React.MouseEvent<HTMLElement>, talents: Talent[]) => {
    event.stopPropagation();
    setTalentPopoverAnchor(event.currentTarget);
    setPopoverTalents(talents);
  }, []);

  const handleTalentPopoverClose = useCallback(() => {
    setTalentPopoverAnchor(null);
    setPopoverTalents([]);
  }, []);

  const handleCopySchedule = useCallback((schedule: TCaseScheduleItem, event: React.MouseEvent) => {
    event.stopPropagation();
    const copyText = formatCopyText(schedule);
    navigator.clipboard.writeText(copyText);
  }, []);

  useEffect(() => {
    if (scheduleDetail?.data && selectedScheduleId) {
      setEditingSchedule(scheduleDetail.data);
    }
  }, [scheduleDetail, selectedScheduleId]);

  const defaultValuesForModal = useMemo(() => {
    if (!editingSchedule) return undefined;
    return convertCaseScheduleToFormValues(editingSchedule);
  }, [editingSchedule]);

  const handleEditSchedule = (schedule: TCaseScheduleItem) => {
    if (schedule.id) {
      setSelectedScheduleId(schedule.id);
      setEditingSchedule(schedule);
      editScheduleModal.open();
    }
  };

  const getMeetingToolLabel = (toolType: number | null | undefined): string => {
    switch (toolType) {
      case MeetingToolType.ZOOM:
        return "Zoom";
      case MeetingToolType.TEAMS:
        return "Microsoft Teams";
      case MeetingToolType.MEET:
        return "Google Meet";
      default:
        return "";
    }
  };

  const getInterviewMethodLabel = (method: number | null | undefined): string => {
    switch (method) {
      case InterviewMethod.ONLINE:
        return "オンライン";
      case InterviewMethod.OFFLINE:
        return "対面";
      default:
        return "";
    }
  };

  const formatCopyText = (schedule: TCaseScheduleItem): string => {
    const parts: string[] = [];

    if (schedule.dateConfirm && schedule.startTimeConfirm) {
      const dateTime = `${schedule.dateConfirm} ${schedule.startTimeConfirm}`;
      parts.push("【面接日時】");
      parts.push(dateTime);
    }

    // Include the matched candidates so the copied text states who the interview is for (one talent per line).
    if (schedule.talents?.length) {
      if (parts.length > 0) parts.push("");
      parts.push("【マッチング人材】");
      schedule.talents.forEach((talent) => parts.push(talent.fullName));
    }

    if (schedule.url) {
      if (parts.length > 0) parts.push("");
      parts.push("【面接URL】");
      parts.push(schedule.url);
    }

    return parts.join("\n");
  };

  const hasCopyableData = (schedule: TCaseScheduleItem): boolean => {
    const hasDateTime = !!(schedule.dateConfirm && schedule.startTimeConfirm);
    const hasUrl = !!schedule.url;
    const hasMeetingInfo = !!(schedule.meetingToolType || schedule.meetingId || schedule.passcode);
    // Candidates are now part of the copied text, so show the copy button when only talents exist too.
    const hasTalents = !!schedule.talents?.length;
    return hasDateTime || hasUrl || hasMeetingInfo || hasTalents;
  };

  const isSchedulePassed = (schedule: TCaseScheduleItem): boolean => {
    if (!schedule.dateConfirm || !schedule.startTimeConfirm) {
      return false;
    }
    const startDateTime = dayjs(`${schedule.dateConfirm} ${schedule.startTimeConfirm}`);

    return startDateTime.isValid() && startDateTime.isBefore(dayjs());
  };

  const handleSubmitSchedule = async (data: TCaseScheduleForm) => {
    const currentScheduleId = selectedScheduleId;
    const scheduleData = convertCaseSchedule(data);

    if (editingSchedule && editingSchedule.id) {
      await updateCaseSchedule(Number(caseId), editingSchedule.id, scheduleData);
    } else {
      await createCaseSchedule(Number(caseId), scheduleData);
    }

    // Invalidate queries to refresh data
    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULES, caseId] });
    if (currentScheduleId) {
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULE_DETAIL, caseId, currentScheduleId] });
    }

    // Modal will close itself after this promise resolves
    setEditingSchedule(null);
    setSelectedScheduleId(undefined);
  };

  const handleDeleteSchedule = async () => {
    if (editingSchedule && editingSchedule.id) {
      const scheduleIdToDelete = editingSchedule.id;
      try {
        await deleteCaseSchedule(Number(caseId), scheduleIdToDelete);

        editScheduleModal.close();
        setEditingSchedule(null);
        setSelectedScheduleId(undefined);

        queryClient.removeQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULE_DETAIL, caseId, scheduleIdToDelete] });

        await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CASE.FETCH_CASE_SCHEDULES, caseId] });
      } catch (error) {
        console.error("Error deleting schedule:", error);
      }
    }
  };

  return (
    <>
      <InnerBox
        mt={4}
        mb={3}
        p={3}
        showDivider={false}
        borderColor={divider.high}
        bordered={true}
        title={
          <Typography variant="sub16Bold" color={black[30]}>
            日程調整
          </Typography>
        }
      >
        {schedules.length > 0 &&
          schedules.map((schedule, index) => {
            const dateFormat = schedule.dateConfirm ? dayjs(schedule.dateConfirm).format(DATE_FORMAT) : null;
            if (dateFormat) {
              schedule.dateConfirm = dateFormat;
            }

            const hasConfirmedDateTime = !!(schedule.dateConfirm && schedule.startTimeConfirm);
            const confirmedDateTime = hasConfirmedDateTime ? `${schedule.dateConfirm} ${schedule.startTimeConfirm}` : null;
            const isExpanded = schedule.id ? (expandedSchedules[schedule.id] ?? true) : true;
            const enrichedTalents = (schedule?.talents || []).map((talent) => ({
              ...talent,
              isRecommendedTalent: talentRecommendedMap.get(Number(talent.id)) ?? false,
            }));

            const scheduleTalents = sortTalentsByRecommended(enrichedTalents);
            const visibleTalents = scheduleTalents.slice(0, MAX_VISIBLE_TALENTS);
            const overflowTalents = scheduleTalents.slice(MAX_VISIBLE_TALENTS);
            const overflowCount = Math.max(0, scheduleTalents.length - MAX_VISIBLE_TALENTS);

            // Format 実施方法 with meeting tool
            const getInterviewMethodWithTool = () => {
              if (!schedule.interviewMethod) return null;
              const methodLabel = getInterviewMethodLabel(schedule.interviewMethod);
              if (Number(schedule.interviewMethod) === InterviewMethod.ONLINE && schedule.meetingToolType) {
                return `${methodLabel}（${getMeetingToolLabel(schedule.meetingToolType)}）`;
              }
              return methodLabel;
            };

            // Format ミーティングID／パスコード combined
            const getMeetingIdPasscode = () => {
              if (Number(schedule.interviewMethod) !== InterviewMethod.ONLINE) return null;
              const parts = [];
              if (schedule.meetingId) parts.push(schedule.meetingId);
              if (schedule.passcode) parts.push(schedule.passcode);
              const meetingInfo = parts.length > 0 ? parts.join("／") : null;
              return meetingInfo ? truncateWithEllipsis(meetingInfo, 30) : null;
            };

            const isPassed = isSchedulePassed(schedule);

            return (
              <InnerBox mt={2} p={0} showDivider={false} borderColor={divider.high} bordered={true} key={schedule.id}>
                {/* Accordion Header */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ px: 2, py: 1.5, cursor: "pointer" }}
                  onClick={() => schedule.id && toggleScheduleExpand(schedule.id)}
                >
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="body14Bold" color={black[30]}>
                      日程{index + 1}
                    </Typography>
                    {hasCopyableData(schedule) && (
                      <Tooltip title="日程情報などをコピー" arrow>
                        <span>
                          <IconBox onClick={(e) => handleCopySchedule(schedule, e)} sx={{ p: 0.5 }}>
                            <IconCopy width={18} height={18} color={black[70]} />
                          </IconBox>
                        </span>
                      </Tooltip>
                    )}
                    {isPassed && <Tag color={black[99]} textColor={text.primary} fontWeight={400} title="面接実施済み" minWidth="auto" />}
                  </Stack>
                  {isExpanded ? <KeyboardArrowUpIcon sx={{ color: black[70] }} /> : <KeyboardArrowDownIcon sx={{ color: black[70] }} />}
                </Stack>

                {/* Collapsible Content */}
                <Collapse in={isExpanded}>
                  <Box sx={{ px: 2, pb: 2 }}>
                    {schedule.dateTimeTalent && (
                      <CaseInfoItem label="面接に関する詳細（候補日時、実地面接詳細等）" value={schedule.dateTimeTalent} type="textArea" />
                    )}

                    {hasConfirmedDateTime && <CaseInfoItem label="面接日時" value={confirmedDateTime} />}

                    {scheduleTalents.length > 0 && (
                      <Box sx={{ borderBottom: `1px solid ${divider.high}`, pb: 1.5, mb: 1.5 }}>
                        <Stack mb={1}>
                          <Typography variant="cap12" color={black[70]}>
                            マッチング人材
                          </Typography>
                        </Stack>
                        <Box>
                          {visibleTalents.map((talent: Talent) => (
                            <Typography key={talent.id} variant="body14" sx={{ display: "block", mb: 0.5 }}>
                              ・{truncateWithEllipsis(talent.fullName, 25)}
                            </Typography>
                          ))}
                          {overflowCount > 0 && (
                            <Typography variant="body14Semi" onClick={(e) => handleTalentOverflowClick(e, overflowTalents)}>
                              他
                              <Typography
                                variant="body14Semi"
                                sx={{
                                  color: blue[80],
                                  textDecoration: "underline",
                                  cursor: "pointer",
                                  mt: 0.5,
                                }}
                              >
                                {overflowCount.toString().padStart(2, "0")}
                              </Typography>
                              名
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    )}

                    {getInterviewMethodWithTool() && (
                      <CaseInfoItem
                        label="実施方法"
                        value={getInterviewMethodWithTool()}
                        isShowDivider={!!(getMeetingIdPasscode() || schedule?.url)}
                      />
                    )}

                    {getMeetingIdPasscode() && (
                      <CaseInfoItem label="ミーティングID／パスコード" value={getMeetingIdPasscode()} isShowDivider={!!schedule?.url} />
                    )}

                    {schedule?.url && <CaseInfoItem label="面接URL" type="link" value={schedule?.url} isShowDivider={false} truncateLength={30} />}

                    {!disabled && !isPassed && (
                      <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditSchedule(schedule)}
                          startIcon={<IconEditor />}
                          sx={{ width: "172px" }}
                        >
                          スケジュール編集
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </InnerBox>
            );
          })}

        {!disabled && !isCompleted && (
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              cursor: "pointer",
              opacity: 1,
              width: "fit-content",
            }}
            onClick={() => {
              setEditingSchedule(null);
              scheduleModal.open();
            }}
            mt={schedules.length > 0 ? 1 : 1.5}
          >
            <AddCircleOutlineIcon
              sx={{
                color: blue[80],
                fontSize: 20,
              }}
            />
            &nbsp;
            <Typography variant="cap12">スケジュールの追加</Typography>
          </Stack>
        )}
      </InnerBox>
      <ScheduleAdjustmentModal
        talents={talents}
        modal={editingSchedule ? editScheduleModal : scheduleModal}
        defaultValues={defaultValuesForModal}
        onSubmit={handleSubmitSchedule}
        onDelete={handleDeleteSchedule}
      />

      <Popover
        open={Boolean(talentPopoverAnchor)}
        anchorEl={talentPopoverAnchor}
        onClose={handleTalentPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              mr: "16px",
              boxShadow: elevation.lv2,
              border: `1px solid ${divider.middle}`,
              borderRadius: rounded.xs,
              maxHeight: 300,
              width: 266,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 1, px: 0, background: background.white }}>
          {popoverTalents.map((talent) => (
            <Box key={talent.id} sx={{ px: 1.5, py: 0.5 }}>
              <Typography variant="body14">・{truncateWithEllipsis(talent.fullName ?? "", 25)}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </>
  );
};
